import { FC } from "react";
import { useSelector } from "react-redux";
import { ChatState } from "../../store/ChatState";
import { ChatDetail, ChatMsgDetail, isChatMsgDetail } from "../../store/ChatAction";
import { ChatServerActionLink, isChatServerActionLink } from "../../xapp/ChatServerMessage";
import "./ExportButton.scss";

export interface ExportButtonProps {
    tabIndex?: string;
}

/**
 * Formats a suggestion chip for export
 */
function formatSuggestion(option: string | ChatServerActionLink): string {
    if (isChatServerActionLink(option)) {
        return option.actionUrl ? `[${option.label}](${option.actionUrl})` : option.label;
    }
    return option;
}

/**
 * Formats chat messages for export
 */
function formatConversation(chats: ChatDetail[]): string {
    return chats
        .filter(isChatMsgDetail)
        .map((chat) => {
            const isVisitor = chat.user.nick?.startsWith("visitor:");
            const role = isVisitor ? "User" : "Assistant";
            const text = chat.msg?.text || "";

            let result = `${role}: ${text}`;

            // Add suggestion chips if present
            const options = chat.msg?.options;
            if (options && options.length > 0) {
                const suggestions = options.map(formatSuggestion).join(", ");
                result += `\nSuggestions: ${suggestions}`;
            }

            return result;
        })
        .join("\n");
}

const ExportButton: FC<ExportButtonProps> = (props) => {
    const chats = useSelector<ChatState, ChatDetail[]>((state) => state.chats);

    const handleClick = async () => {
        const conversation = formatConversation(chats);
        try {
            await navigator.clipboard.writeText(conversation);
        } catch {
            // Fallback for older browsers using deprecated execCommand
            // Note: execCommand is deprecated and may be removed in future browsers
            console.warn("[ExportButton] Using deprecated execCommand fallback for clipboard copy");
            const textarea = document.createElement("textarea");
            textarea.value = conversation;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
    };

    return (
        <div
            id="xapp-widget-export"
            aria-label="export conversation"
            aria-hidden={false}
            tabIndex={props.tabIndex ? Number(props.tabIndex) : 0}
            className="export-button"
            onClick={handleClick}
        />
    );
};

export default ExportButton;
